import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Match human-entered brand names without weakening native product search.
 * @param {unknown} value
 * @returns {string}
 */
const normalizeBrandQuery = (value) =>
  String(value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pl-PL');

/**
 * ETOLIE 06.09.2026: Shopify dopasowuje pełne słowa, więc urwany wyraz w trakcie
 * pisania („tren", „mary") daje zero wyników. Gwiazdka na końcu ostatniego wyrazu
 * włącza natywne dopasowanie początku słowa, ale jednocześnie wyłącza tolerancję
 * literówek i rozumienie zdań — dlatego doklejamy ją WYŁĄCZNIE awaryjnie i tylko
 * dla zapytań bez własnej składni (gwiazdka, cudzysłów, AND/OR/NOT, operatory).
 * @param {string} searchTerm - Przycięte zapytanie użytkowniczki.
 * @returns {string | null} Zapytanie z gwiazdką albo null, gdy nie wolno próbować.
 */
const buildPrefixQuery = (searchTerm) => {
  const term = String(searchTerm ?? '').trim();
  if (!term || term.includes('*') || term.includes('"') || term.includes("'")) return null;

  const words = term.split(/\s+/);
  if (words.some((word) => /^(?:AND|OR|NOT)$/.test(word) || /^[:()+-]/.test(word))) return null;

  const lastWord = words[words.length - 1] ?? '';
  if (!/^[\p{L}\p{N}]{3,}$/u.test(lastWord)) return null;

  return `${term}*`;
};

/**
 * ETOLIE 06.09.2026: liczba produktów w odpowiedzi sekcji, czytana z atrybutu
 * wystawianego przez sections/predictive-search.liquid. Brak atrybutu = nie wiemy,
 * więc nie uruchamiamy dopasowania początku słowa.
 * @param {string} markup
 * @returns {number | null}
 */
const readProductCount = (markup) => {
  const match = /data-etolie-results-count="(\d+)"/.exec(markup);
  return match ? Number(match[1]) : null;
};

/**
 * @param {number} count
 * @returns {string}
 */
const formatProductCount = (count) => `${count} ${window.etolieProductNoun(count)}`;

/**
 * ETOLIE 29.09.2026 (audyt wyszukiwarki, S1): „bordowa torebka” dawała 0 z 8 bordowych
 * torebek na pierwszych miejscach, bo Shopify szuka słów, a kolor jest w wariancie.
 * Fraza z JEDNYM rozpoznanym kolorem i co najmniej jednym innym słowem idzie na stronę
 * wyników z filtrem całej rodziny koloru (te same wartości co wiersz koloru w panelu).
 * Kolor nierozpoznany, dwa różne kolory albo sam kolor = zwykłe wyszukiwanie.
 * Ta sama funkcja jest w assets/search-page-input.js (pole na stronie wyników).
 */
const AUTO_COLOR_PARAM = 'kolor_auto';

/** @param {unknown} value */
const foldSearchWord = (value) =>
  String(value ?? '')
    .toLocaleLowerCase('pl-PL')
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Odmiana nazwy koloru: czarny → czarna/czarne, niebieski → niebieska, bordo → bordowa,
 * DE schwarz → schwarze/schwarzes, beige → beiges.
 * @param {string} word
 * @returns {string[]}
 */
const colorWordForms = (word) => {
  const stem = word.slice(0, -1);
  const forms = [word];
  if (word.endsWith('y')) forms.push(`${stem}a`, `${stem}e`, `${stem}ej`);
  else if (word.endsWith('i')) forms.push(`${stem}a`, `${stem}ie`, `${stem}iej`);
  else if (word.endsWith('o')) forms.push(`${word}wy`, `${word}wa`, `${word}we`, `${word}wej`);
  if (word.endsWith('e')) forms.push(`${word}s`, `${word}r`, `${word}n`, `${word}m`);
  else forms.push(`${word}e`, `${word}es`, `${word}er`, `${word}en`, `${word}em`);
  return forms;
};

/**
 * @param {string} query
 * @param {Element | null} registryElement - <script data-etolie-color-registry>
 * @returns {{ param: string; values: string[] } | null}
 */
const findColorFilter = (query, registryElement) => {
  if (!(registryElement instanceof HTMLScriptElement)) return null;
  const words = String(query ?? '').trim().split(/\s+/).map(foldSearchWord).filter(Boolean);
  if (words.length < 2 || words.some((word) => /[*"'():]/.test(word) || /^(and|or|not)$/.test(word))) return null;

  let registry;
  try {
    registry = JSON.parse(registryElement.textContent || '{}');
  } catch {
    return null;
  }
  const param = typeof registry?.param === 'string' ? registry.param : '';
  const families = (Array.isArray(registry?.families) ? registry.families : [])
    .filter((family) => Array.isArray(family?.values) && family.values.length)
    .map((family) => {
      const forms = new Set();
      [family.name, ...family.values].forEach((value) => {
        const word = foldSearchWord(value).trim();
        // „bez” (Beż) to też polskie „bez” w „sukienka bez rękawów”.
        if (word.length < 3 || /[\s,-]/.test(word) || word === 'bez') return;
        colorWordForms(word).forEach((form) => forms.add(form));
      });
      return { values: family.values.map(String), forms };
    });
  if (!param.startsWith('filter.') || !families.length) return null;

  const matched = new Set();
  let otherWords = 0;
  for (const word of words) {
    const family = families.find((entry) => entry.forms.has(word));
    if (family) matched.add(family);
    else otherWords += 1;
  }
  if (matched.size !== 1 || otherWords === 0) return null;
  const [family] = matched;
  return { param, values: family.values };
};

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLButtonElement} [viewAllButton] - The native search / exact-brand submit button.
 * @property {HTMLButtonElement} [fieldSubmitButton] - The arrow submit button beside the search field.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #brandEntries = null;

  #categoryEntries = null;

  #defaultViewAllLabel = '';

  #requestId = 0;

  /**
   * ETOLIE 06.09.2026: zapytanie z gwiazdką, które realnie dało wyniki dla obecnej
   * treści pola. Enter, przycisk strzałki i „Zobacz wszystkie produkty" mają wysłać
   * to samo zapytanie, które klientka widzi w podglądzie. Zerowane przy każdej
   * zmianie pola; `term` pilnuje, żeby nie użyć go do innej frazy.
   * @type {{ term: string; query: string } | null}
   */
  #prefixFallback = null;

  #focusTimer = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#defaultViewAllLabel = this.refs.viewAllButton?.textContent?.trim() ?? '';
    this.#syncFieldSubmitState();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    if (this.#focusTimer) window.clearTimeout(this.#focusTimer);
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    if (this.#focusTimer) window.clearTimeout(this.#focusTimer);
    this.#focusTimer = null;
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    const configuredDelay = Number(this.dataset.focusDelay);
    const delay = prefersReducedMotion() ? 0 : Number.isFinite(configuredDelay) ? configuredDelay : 0;
    this.#focusTimer = window.setTimeout(() => {
      const dialogElement = this.dialog?.refs.dialog;
      if (dialogElement?.open && !dialogElement.classList.contains('dialog-closing')) {
        this.refs.searchInput.focus({ preventScroll: true });
      }
      this.#focusTimer = null;
    }, delay);
  };

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[ref="resultsItems[]"]')).filter((item) => item instanceof HTMLElement)
    );
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        if (item.getAttribute('role') === 'option') {
          item.setAttribute('aria-selected', 'false');
        } else {
          item.removeAttribute('aria-selected');
        }
      }
    }

    if (activeItem?.id) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Clear the active-descendant state without moving DOM focus. This is used
   * when Tab leaves the combobox; listbox options stay outside the tab order.
   */
  #clearResultSelection() {
    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
      item.setAttribute('aria-selected', 'false');
    });
    this.refs.searchInput.removeAttribute('aria-activedescendant');
    this.#isKeyboardNavigation = false;
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.defaultPrevented) return;

    if (event.key === 'Escape') return;

    if (event.key === 'Tab') {
      this.#clearResultSelection();
      return;
    }

    if (event.key === 'Enter') {
      const exactBrand = this.#findExactBrand(this.refs.searchInput.value);
      const exactCategory = exactBrand ? null : this.#findExactCategory(this.refs.searchInput.value);
      const exactIntent = exactBrand ?? exactCategory;

      // While a new request owns the result surface, never inspect stale
      // selection or single-result markers. Default submit remains available.
      if (this.#resultsAreBusy()) {
        if (exactIntent) {
          event.preventDefault();
          this.#navigateToBrand(exactIntent);
        }
        return;
      }

      if (this.#currentIndex >= 0) {
        const selectedLink = this.#currentItem;
        if (selectedLink instanceof HTMLAnchorElement) {
          const selectedUrl = new URL(selectedLink.href, location.origin);
          if (selectedUrl.origin === location.origin) {
            event.preventDefault();
            window.location.assign(selectedUrl.href);
            return;
          }
        }
      }

      if (exactIntent) {
        event.preventDefault();
        this.#navigateToBrand(exactIntent);
        return;
      }

      const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
        event.preventDefault();
        window.location.href = singleResultContainer.dataset.singleResultUrl;
        return;
      }

      if (!this.#allResultsItems?.length) return;

      event.preventDefault();
      window.location.href = this.#buildSearchPageUrl(this.#getSubmitQuery());
      return;
    }

    if (this.#resultsAreBusy()) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') event.preventDefault();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
    }
  };

  /**
   * Exact brand submits use the collection canonical; every other query keeps
   * the native product-only form submission unchanged.
   * @param {SubmitEvent} event
   */
  submitSearch = (event) => {
    const exactBrand = this.#findExactBrand(this.refs.searchInput.value);
    const exactCategory = exactBrand ? null : this.#findExactCategory(this.refs.searchInput.value);
    const exactIntent = exactBrand ?? exactCategory;
    if (exactIntent) {
      event.preventDefault();
      this.#navigateToBrand(exactIntent);
      return;
    }

    // ETOLIE 06.09.2026: ta sama ścieżka obsługuje przycisk strzałki, „Zobacz wszystkie
    // produkty" i Enter bez wyników. Gdy podgląd pokazał wyniki dzięki awaryjnej
    // gwiazdce, wysyłamy tę samą frazę; bez niej natywna wysyłka formularza zostaje.
    // 29.09.2026 (S1): fraza z kolorem też idzie własnym adresem (filtr koloru).
    const submitQuery = this.#getSubmitQuery();
    if (submitQuery === this.refs.searchInput.value.trim() && !this.#findColorFilter()) return;

    event.preventDefault();
    window.location.href = this.#buildSearchPageUrl(submitQuery);
  };

  /**
   * ETOLIE 06.09.2026: adres strony wyników z tymi samymi parametrami, które ma
   * formularz modala (type=product, options[prefix]=last).
   * @param {string} query
   * @returns {string}
   */
  #buildSearchPageUrl(query) {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[prefix]', 'last');
    const colorFilter = this.#findColorFilter();
    if (colorFilter) {
      colorFilter.values.forEach((value) => searchUrl.searchParams.append(colorFilter.param, value));
      searchUrl.searchParams.set(AUTO_COLOR_PARAM, '1');
    }
    return searchUrl.toString();
  }

  /** Filtr koloru dla tego, co klientka widzi w polu (S1, 29.09.2026). */
  #findColorFilter() {
    return findColorFilter(this.refs.searchInput.value, this.querySelector('[data-etolie-color-registry]'));
  }

  /**
   * Parse the server-rendered `main-menu > Marki` registry once per component.
   * Invalid and duplicate entries fail closed into ordinary product search.
   * @returns {{ title: string; normalizedTitle: string; url: string; handle: string; productCount: number | null }[]}
   */
  #getBrandEntries() {
    if (this.#brandEntries) return this.#brandEntries;

    const registryElement = this.querySelector('[data-etolie-brand-registry]');
    if (!(registryElement instanceof HTMLScriptElement)) {
      this.#brandEntries = [];
      return this.#brandEntries;
    }

    try {
      const rawEntries = JSON.parse(registryElement.textContent || '[]');
      const seenNames = new Set();
      const entries = [];

      for (const rawEntry of Array.isArray(rawEntries) ? rawEntries : []) {
        const title = typeof rawEntry?.title === 'string' ? rawEntry.title.trim() : '';
        const normalizedTitle = normalizeBrandQuery(title);
        const rawUrl = typeof rawEntry?.url === 'string' ? rawEntry.url.trim() : '';
        const handle = typeof rawEntry?.handle === 'string' ? rawEntry.handle.trim() : '';
        if (!normalizedTitle || !rawUrl || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) || seenNames.has(normalizedTitle)) continue;

        const parsedUrl = new URL(rawUrl, location.origin);
        if (parsedUrl.origin !== location.origin || parsedUrl.pathname !== `${window.Shopify?.routes?.root || '/'}collections/${handle}`) continue;

        const parsedCount = Number(rawEntry?.productCount);
        const productCount = Number.isInteger(parsedCount) && parsedCount >= 0 ? parsedCount : null;

        seenNames.add(normalizedTitle);
        entries.push({
          title,
          normalizedTitle,
          url: parsedUrl.pathname,
          handle,
          productCount,
        });
      }

      this.#brandEntries = entries;
    } catch (error) {
      console.warn('ETOLIE brand search registry is invalid; using native product search.', error);
      this.#brandEntries = [];
    }

    return this.#brandEntries;
  }

  /**
   * @param {string} query
   * @returns {{ title: string; normalizedTitle: string; url: string; productCount: number | null } | null}
   */
  #findExactBrand(query) {
    const normalizedQuery = normalizeBrandQuery(query);
    if (!normalizedQuery) return null;

    return this.#getBrandEntries().find((brand) => brand.normalizedTitle === normalizedQuery) ?? null;
  }

  /**
   * Parse the server-rendered `main-menu > Kategorie` registry once. Menu state
   * owns every canonical URL; the registry adds only explicit language aliases.
   * Invalid entries fail closed into ordinary product search.
   * @returns {{ title: string; url: string; handle: string; normalizedAliases: string[] }[]}
   */
  #getCategoryEntries() {
    if (this.#categoryEntries) return this.#categoryEntries;

    const registryElement = this.querySelector('[data-etolie-category-registry]');
    if (!(registryElement instanceof HTMLScriptElement)) {
      this.#categoryEntries = [];
      return this.#categoryEntries;
    }

    try {
      const rawEntries = JSON.parse(registryElement.textContent || '[]');
      const seenAliases = new Set();
      const entries = [];

      for (const rawEntry of Array.isArray(rawEntries) ? rawEntries : []) {
        const title = typeof rawEntry?.title === 'string' ? rawEntry.title.trim() : '';
        const rawUrl = typeof rawEntry?.url === 'string' ? rawEntry.url.trim() : '';
        const handle = typeof rawEntry?.handle === 'string' ? rawEntry.handle.trim() : '';
        if (!title || !rawUrl || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) continue;

        const parsedUrl = new URL(rawUrl, location.origin);
        if (parsedUrl.origin !== location.origin || parsedUrl.pathname !== `${window.Shopify?.routes?.root || '/'}collections/${handle}`) continue;

        const normalizedAliases = [title, ...(Array.isArray(rawEntry?.aliases) ? rawEntry.aliases : [])]
          .filter((alias) => typeof alias === 'string')
          .map((alias) => normalizeBrandQuery(alias))
          .filter(Boolean)
          .filter((alias, index, aliases) => aliases.indexOf(alias) === index)
          .filter((alias) => {
            if (seenAliases.has(alias)) return false;
            seenAliases.add(alias);
            return true;
          });
        if (!normalizedAliases.length) continue;

        entries.push({ title, url: parsedUrl.pathname, handle, normalizedAliases });
      }

      this.#categoryEntries = entries;
    } catch (error) {
      console.warn('ETOLIE category search registry is invalid; using native product search.', error);
      this.#categoryEntries = [];
    }

    return this.#categoryEntries;
  }

  /**
   * @param {string} query
   * @returns {{ title: string; url: string; handle: string; normalizedAliases: string[] } | null}
   */
  #findExactCategory(query) {
    const normalizedQuery = normalizeBrandQuery(query);
    if (!normalizedQuery) return null;

    return this.#getCategoryEntries().find((category) => category.normalizedAliases.includes(normalizedQuery)) ?? null;
  }

  /**
   * Keep the persistent footer in sync while the result fragment is loading.
   * @param {{ title: string; normalizedTitle: string; url: string; productCount: number | null } | null} brand
   * @param {string} [query]
   */
  #setBrandIntent(brand, query = this.refs.searchInput.value) {
    const category = brand ? null : this.#findExactCategory(query);
    this.toggleAttribute('data-exact-brand', Boolean(brand));
    this.toggleAttribute('data-exact-category', Boolean(category));

    const viewAllButton = this.refs.viewAllButton;
    if (!viewAllButton) return;

    if (brand) {
      const prefix = this.dataset.brandFooterLabel || 'Zobacz całą kolekcję';
      viewAllButton.textContent = `${prefix} ${brand.title}`;
    } else if (category) {
      const prefix = this.dataset.categoryFooterLabel || 'Zobacz kategorię';
      viewAllButton.textContent = `${prefix} ${category.title}`;
    } else {
      viewAllButton.textContent = this.#defaultViewAllLabel;
    }
  }

  /**
   * @param {{ url: string } | null} brand
   */
  #navigateToBrand(brand) {
    if (!brand?.url) return;
    window.location.assign(brand.url);
  }

  /**
   * ETOLIE 06.09.2026: fraza, którą wysyłamy na stronę wyników. Zwykle to dokładnie
   * to, co klientka wpisała; tylko gdy podgląd pokazał wyniki dzięki awaryjnej
   * gwiazdce, wysyłamy tę samą skuteczną frazę, żeby strona nie była pusta.
   * @returns {string}
   */
  #getSubmitQuery() {
    const value = this.refs.searchInput.value.trim();
    if (this.#prefixFallback?.term === value) return this.#prefixFallback.query;
    return value;
  }

  #resultsAreBusy() {
    return this.refs.predictiveSearchResults.getAttribute('aria-busy') === 'true';
  }

  #syncFieldSubmitState() {
    const fieldSubmitButton = this.refs.fieldSubmitButton;
    if (!fieldSubmitButton) return;
    fieldSubmitButton.disabled = this.refs.searchInput.value.trim().length === 0;
  }

  /**
   * The old fragment remains visible during debounce/fetch to prevent layout
   * jumps, but it must never remain actionable for pointer or keyboard users.
   * @param {boolean} isBusy
   */
  #setResultsBusy(isBusy) {
    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.setAttribute('aria-busy', String(isBusy));
    predictiveSearchResults.toggleAttribute('inert', isBusy);
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (!keepFocus && this.refs.searchInput.value.trim().length > 0) return;
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  #debouncedSearch = debounce((searchTerm, requestId) => {
    this.#getSearchResults(searchTerm, requestId);
  }, 180);

  /**
   * Start autocomplete for every real input change (keyboard, paste, clear or
   * assistive technology), while avoiding incomplete IME compositions.
   * @param {InputEvent} event
   */
  search = (event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#syncFieldSubmitState();
    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;
    this.#requestId += 1;
    const requestId = this.#requestId;

    this.#activeFetch?.abort();
    this.#activeFetch = null;
    // ETOLIE 06.09.2026: każda zmiana pola unieważnia awaryjne zapytanie z gwiazdką.
    this.#prefixFallback = null;
    this.#clearExactBrandDecoration();
    this.#setBrandIntent(this.#findExactBrand(searchTerm));

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#setResultsBusy(true);
    if (event.isComposing) return;
    this.#debouncedSearch(searchTerm, requestId);
  };

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * ETOLIE 06.09.2026: jedno żądanie podglądu wyników — wydzielone, żeby awaryjne
   * zapytanie z gwiazdką szło DOKŁADNIE tym samym mechanizmem i z tymi samymi
   * parametrami co zwykłe.
   * @param {string} query - Zapytanie wysyłane do Shopify.
   * @returns {Promise<string>} Markup sekcji wyników.
   */
  #fetchResultsMarkup(query) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    // 18.10.2026: 10 kandydatów (maksimum Shopify), okno pokazuje 8 po podbiciu koloru z hasła.
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for.
   * @param {number} requestId - Monotonic guard against stale debounced responses.
   */
  async #getSearchResults(searchTerm, requestId) {
    if (!this.dataset.sectionId || requestId !== this.#requestId) return;

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      let resultsMarkup = await this.#fetchResultsMarkup(searchTerm);
      if (abortController.signal.aborted || requestId !== this.#requestId) return;
      if (!resultsMarkup) throw new Error('No predictive search markup returned.');
      if (normalizeBrandQuery(this.refs.searchInput.value) !== normalizeBrandQuery(searchTerm)) {
        throw new Error('Predictive search query changed without an input event.');
      }

      // ETOLIE 06.09.2026: zero wyników w trakcie pisania („tren") to najczęściej urwany
      // ostatni wyraz, nie brak towaru. Pytamy Shopify jeszcze raz z gwiazdką i pokazujemy
      // te wyniki bez żadnej dodatkowej linii; gdy i one są puste, zostaje stan pusty
      // dla pierwotnego zapytania. Guardy (numer żądania, abort, treść pola) te same.
      const prefixQuery = readProductCount(resultsMarkup) === 0 ? buildPrefixQuery(searchTerm) : null;
      if (prefixQuery) {
        const prefixMarkup = await this.#fetchResultsMarkup(prefixQuery);
        if (abortController.signal.aborted || requestId !== this.#requestId) return;
        if (normalizeBrandQuery(this.refs.searchInput.value) !== normalizeBrandQuery(searchTerm)) {
          throw new Error('Predictive search query changed without an input event.');
        }
        if (prefixMarkup && readProductCount(prefixMarkup) > 0) {
          resultsMarkup = prefixMarkup;
          this.#prefixFallback = { term: searchTerm, query: prefixQuery };
        }
      }

      window.etolieDestroyCards?.(predictiveSearchResults);
      morph(predictiveSearchResults, resultsMarkup);
      window.etolieInitCards?.(predictiveSearchResults);
      this.#arrangeProductResults();
      this.#decorateExactBrandResults(searchTerm);
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted || requestId !== this.#requestId) return;
      this.#showSearchFailure();
      console.error('Predictive search request failed.', error);
    } finally {
      if (!abortController.signal.aborted && requestId === this.#requestId) {
        this.#setResultsBusy(false);
        if (this.#activeFetch === abortController) this.#activeFetch = null;
      }
    }
  }

  /**
   * ETOLIE 18.10.2026 (Jarek): „sukienka czarna” pokazywało najpierw kremową „z czarnym wykończeniem”
   * i zebrę, bo Shopify szuka słów, a kolor jest w wariancie. Ta sama rodzina koloru, która na stronie
   * wyników daje filtr (findColorFilter), tu przesuwa na początek kafelki, których opcja Kolor
   * (data-etolie-colors z predictive-search-products-list) należy do rodziny. Prosimy Shopify o 10
   * produktów, zostaje 8 — tyle, ile okno pokazywało dotąd. Kafelek wielokolorowy dostaje od razu
   * próbkę koloru z hasła (klik w swatch = etolie-card.js: zdjęcie, etykieta, cena, rozmiary),
   * o ile ten kolor jest wśród 3 pokazanych próbek i nie jest wyprzedany.
   */
  #arrangeProductResults() {
    const list = this.refs.predictiveSearchResults.querySelector(
      '#predictive-search-products .predictive-search-results__wrapper-products'
    );
    if (!(list instanceof HTMLElement)) return;
    const cards = Array.from(list.children).filter((item) => item instanceof HTMLElement);
    const colorFilter = this.#findColorFilter();
    if (colorFilter) {
      const wanted = new Set(colorFilter.values.map((value) => foldSearchWord(value).trim()));
      const inFamily = (value) => wanted.has(foldSearchWord(value).trim());
      const matches = (card) => String(card.dataset.etolieColors || '').split('~').some(inFamily);
      const matched = cards.filter(matches);
      if (matched.length) list.replaceChildren(...matched, ...cards.filter((card) => !matches(card)));
      matched.forEach((card) => {
        const active = card.querySelector('.etolie-card__swatch.is-active');
        if (active instanceof HTMLElement && inFamily(active.dataset.color || '')) return;
        const swatch = Array.from(card.querySelectorAll('.etolie-card__swatch[data-color]')).find(
          (item) =>
            item instanceof HTMLElement &&
            !item.classList.contains('etolie-card__swatch--out') &&
            inFamily(item.dataset.color || '')
        );
        if (swatch instanceof HTMLElement) swatch.click();
      });
    }
    Array.from(list.children)
      .slice(8)
      .forEach((card) => card.remove());
  }

  #showSearchFailure() {
    const resultTemplate = this.querySelector('[data-etolie-search-error-template]');
    const errorResult =
      resultTemplate instanceof HTMLTemplateElement
        ? resultTemplate.content.firstElementChild?.cloneNode(true)
        : null;

    if (errorResult instanceof HTMLElement) {
      this.refs.predictiveSearchResults.replaceChildren(errorResult);
    } else {
      this.refs.predictiveSearchResults.replaceChildren();
    }
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  #clearExactBrandDecoration() {
    this.refs.predictiveSearchResults.querySelector('[data-etolie-brand-result]')?.remove();

    const previewTitleLabel = this.refs.predictiveSearchResults.querySelector(
      '[data-predictive-products-title-label]'
    );
    if (previewTitleLabel instanceof HTMLElement && previewTitleLabel.dataset.nativeLabel) {
      previewTitleLabel.textContent = previewTitleLabel.dataset.nativeLabel;
      delete previewTitleLabel.dataset.nativeLabel;
    }

    const previewCounter = this.refs.predictiveSearchResults.querySelector('[data-etolie-brand-preview-count]');
    if (previewCounter instanceof HTMLElement) {
      previewCounter.hidden = true;
      previewCounter.textContent = '';
    }

    const noResults = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__no-results');
    if (noResults instanceof HTMLElement && noResults.dataset.nativeText) {
      noResults.textContent = noResults.dataset.nativeText;
      delete noResults.dataset.nativeText;
    }
  }

  /**
   * Add a premium exact-brand route above the native product preview. The
   * collection total and the number of rendered cards are deliberately shown
   * as separate values, so an eight-card preview never pretends to be complete.
   * @param {string} searchTerm
   */
  #decorateExactBrandResults(searchTerm) {
    const brand = this.#findExactBrand(searchTerm);
    this.#setBrandIntent(brand);
    if (!brand) return;

    const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    const resultTemplate = this.querySelector('[data-etolie-brand-result-template]');
    if (!(resultsInner instanceof HTMLElement) || !(resultTemplate instanceof HTMLTemplateElement)) return;

    const brandResult = resultTemplate.content.firstElementChild?.cloneNode(true);
    if (!(brandResult instanceof HTMLElement)) return;
    brandResult.dataset.etolieBrandHandle = brand.handle;

    const brandLink = brandResult.querySelector('[data-etolie-brand-link]');
    const brandTitle = brandResult.querySelector('[data-etolie-brand-title]');
    const brandCount = brandResult.querySelector('[data-etolie-brand-count]');
    if (!(brandLink instanceof HTMLAnchorElement) || !(brandTitle instanceof HTMLElement)) return;

    brandLink.href = brand.url;
    brandLink.setAttribute('aria-label', `Zobacz pełną kolekcję marki ${brand.title}`);
    brandTitle.textContent = brand.title;
    if (brandCount instanceof HTMLElement) {
      brandCount.textContent = brand.productCount === null ? 'Pełna kolekcja' : formatProductCount(brand.productCount);
    }

    const liveStatus = resultsInner.querySelector('[role="status"]');
    if (liveStatus instanceof HTMLElement) {
      liveStatus.insertAdjacentElement('afterend', brandResult);
    } else {
      resultsInner.prepend(brandResult);
    }

    const previewCards = resultsInner.querySelectorAll(
      '#predictive-search-products .predictive-search-results__wrapper-products .predictive-search-results__card--product'
    );
    const previewCount = previewCards.length;
    const knownTotal = brand.productCount !== null && brand.productCount >= previewCount ? brand.productCount : null;
    const previewTitle = resultsInner.querySelector('[data-predictive-products-title]');
    const previewTitleLabel = previewTitle?.querySelector('[data-predictive-products-title-label]');
    const previewCounter = previewTitle?.querySelector('[data-etolie-brand-preview-count]');

    if (previewTitleLabel instanceof HTMLElement) {
      previewTitleLabel.dataset.nativeLabel = previewTitleLabel.textContent?.trim() || '';
      previewTitleLabel.textContent = this.dataset.brandPreviewLabel || 'Wybrane produkty';
    }
    if (previewCounter instanceof HTMLElement) {
      previewCounter.hidden = false;
      previewCounter.textContent = knownTotal === null ? `${previewCount} w podglądzie` : `${previewCount} z ${knownTotal}`;
    }

    if (liveStatus instanceof HTMLElement) {
      const collectionSummary = knownTotal === null ? 'Pełna kolekcja jest dostępna pod linkiem marki.' : `${formatProductCount(knownTotal)} w kolekcji.`;
      liveStatus.textContent = `Dopasowana marka ${brand.title}. ${formatProductCount(previewCount)} w podglądzie. ${collectionSummary}`;
    }

    const noResults = resultsInner.querySelector('.predictive-search-results__no-results');
    if (previewCount === 0 && noResults instanceof HTMLElement) {
      noResults.dataset.nativeText = noResults.textContent?.trim() || '';
      noResults.textContent = 'Pełna oferta marki jest dostępna w kolekcji.';
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#requestId += 1;
    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#prefixFallback = null;
    this.#clearExactBrandDecoration();
    this.#setBrandIntent(null, '');
    searchInput.value = '';
    this.#syncFieldSubmitState();
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
    this.#hideResetButton();

    const emptyTemplate = this.querySelector('[data-etolie-search-empty-template]');
    window.etolieDestroyCards?.(predictiveSearchResults);
    if (emptyTemplate instanceof HTMLTemplateElement) {
      predictiveSearchResults.replaceChildren(emptyTemplate.content.cloneNode(true));
      // 18.10.2026: kafelki listingu w pustym stanie (swatche, galeria) po wstawieniu z szablonu.
      window.etolieInitCards?.(predictiveSearchResults);
    } else {
      predictiveSearchResults.replaceChildren();
    }
    this.#setResultsBusy(false);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
